import { Plus, TestTubes } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  type Column,
} from '@/components/ui';
import { formatRM } from '@/features/billing/money';
import { useBranch } from '@/features/branch/branch-context';
import { useLabPackages } from '@/features/lab-packages/api';
import type { LabPackage } from '@/features/lab-packages/types';
import styles from '@/pages/queue/queue.module.css';
import { LabPackageDialog } from './LabPackageDialog';
import own from './LabPackages.module.css';

/**
 * "Blood test packages" under Services & prices (RL-1138, API RL-988):
 * screening packages patients book in the app, at the lab or at home, in app
 * order. Price changes apply to new bookings only; a package is switched off,
 * never deleted.
 */
export function LabPackagesCard({ can }: { can: boolean }) {
  const { branches } = useBranch();
  const [all, setAll] = useState(false);
  const [editing, setEditing] = useState<LabPackage | 'new' | null>(null);
  const q = useLabPackages(all);
  const branchName = (id: string) =>
    branches.find((b) => b.id === id)?.name ?? 'Other branch';

  const columns: Column<LabPackage>[] = [
    {
      key: 'name',
      header: 'Package',
      render: (p) => (
        <>
          <p className={styles.name}>{p.name}</p>
          {p.nameMs && (
            <p className={styles.sub} lang="ms">
              {p.nameMs}
            </p>
          )}
          {!p.isActive && <Badge>Switched off</Badge>}
        </>
      ),
    },
    {
      key: 'tests',
      header: 'Tests',
      render: (p) => (
        <>
          <p>
            {p.tests.length} {p.tests.length === 1 ? 'test' : 'tests'}
          </p>
          <p className={styles.sub}>{p.tests.join(', ')}</p>
        </>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      render: (p) => formatRM(p.priceSen),
    },
    {
      key: 'member',
      header: 'Member price',
      align: 'right',
      render: (p) =>
        p.memberPriceSen === null ? '—' : formatRM(p.memberPriceSen),
    },
    {
      key: 'booking',
      header: 'Booking',
      render: (p) => (
        <>
          <p>Valid {p.validityDays} days</p>
          <p className={styles.sub}>
            {p.fastingHours ? `Fast ${p.fastingHours} h` : 'No fasting'}
            {' · '}
            {p.homeVisitAllowed ? 'Home visit allowed' : 'At the lab only'}
          </p>
        </>
      ),
    },
    {
      key: 'branches',
      header: 'Branches',
      render: (p) =>
        p.branchIds.length ? p.branchIds.map(branchName).join(', ') : 'All',
    },
    {
      key: 'app',
      header: 'In the app',
      render: (p) => (
        <div className={styles.badges}>
          <Badge tone={p.sellInApp && p.isActive ? 'success' : 'neutral'} dot>
            {p.sellInApp && p.isActive ? 'Sold in the app' : 'Not in the app'}
          </Badge>
          {p.panelCovered && <Badge tone="info">Panels may cover</Badge>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (p) =>
        can ? (
          <Button
            size="sm"
            onClick={() => setEditing(p)}
            aria-label={`Edit ${p.name}`}
          >
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      title="Blood test packages"
      flush
      actions={
        <div className={own.filters}>
          <label className={own.check}>
            <input
              type="checkbox"
              checked={all}
              onChange={(e) => setAll(e.target.checked)}
            />
            Show switched-off packages
          </label>
          {can && (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={14} />}
              onClick={() => setEditing('new')}
            >
              Add package
            </Button>
          )}
        </div>
      }
    >
      <DataTable
        caption="Blood test and screening packages"
        columns={columns}
        rows={q.data}
        rowKey={(p) => p.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            icon={<TestTubes size={22} />}
            title={all ? 'No packages yet' : 'No packages on sale'}
            body={
              can
                ? 'Add a package of lab tests patients can book in the app, at the lab or at home.'
                : undefined
            }
          />
        }
      />
      {editing && (
        <LabPackageDialog
          pkg={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}
